import { useState } from "react";
import PLANS from "../utils/plansv2";
import { createSession } from "../api/user";
import { useAuth } from "../contexts/AuthContext";
import ContactSaleModal from "./ContactSaleModal";

const PricingPlansDashboard = ({
  isMinimal = false,
  showHeader = true,
  showBillingToggle = true,
  onPlanSelect,
  disableFreePlan = false,
}) => {
  const { user, organization } = useAuth();
  const [isAnnual, setIsAnnual] = useState(true);
  const [showGroupPlans, setShowGroupPlans] = useState(false);
  const [expandedPlans, setExpandedPlans] = useState(
    new Set(PLANS.map((p) => p.id)),
  );
  const [showContactSaleModal, setShowContactSaleModal] = useState(false);
  const [selectedPlanForContactSale, setSelectedPlanForContactSale] =
    useState(null);

  // Payment country and currency come from the organization, not the company
  const paymentCountry = organization?.payment_country || "US";
  const isINR = paymentCountry === "IN";
  const currency = isINR ? "INR" : "USD";
  const currencySymbol = isINR ? "₹" : "$";

  const toggleBillingCycle = () => setIsAnnual((v) => !v);

  const togglePlanFeatures = (planId) => {
    setExpandedPlans((prev) => {
      const next = new Set(prev);
      next.has(planId) ? next.delete(planId) : next.add(planId);
      return next;
    });
  };

  // For INR users, use the _IN plan ID variant when calling the backend
  const effectivePlanId = (plan) => (isINR && plan.idIN ? plan.idIN : plan.id);

  const getPrice = (plan) => {
    if (plan.contact) return null;
    const price = isINR
      ? isAnnual
        ? plan.priceAnnualIN
        : plan.priceMonthlyIN
      : isAnnual
        ? plan.priceAnnual
        : plan.priceMonthly;
    if (price == null) return null;
    return price.toLocaleString("en-US");
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const proceedRazorpaySubscription = async (subscriptionId, razorpayKeyId) => {
    const res = await loadRazorpay();
    if (!res) {
      alert("Razorpay SDK failed to load");
      return;
    }
    const options = {
      key: razorpayKeyId,
      subscription_id: subscriptionId,
      name: "EzSignly",
      description: "EzSignly Subscription",
      handler: () => {},
      prefill: { name: user?.name, email: user?.email },
      theme: { color: "#1A6EDB" },
      config: {
        display: {
          blocks: {
            banks: {
              name: "Pay using",
              instruments: [
                { method: "upi" },
                { method: "card" },
                { method: "wallet" },
              ],
            },
          },
          sequence: ["block.banks"],
          preferences: {
            show_default_blocks: false,
          },
        },
      },
    };
    new window.Razorpay(options).open();
  };

  const handlePlanSelection = async (plan, subscriptionType) => {
    try {
      if (onPlanSelect) {
        onPlanSelect(effectivePlanId(plan), subscriptionType);
        return;
      }
      const response = await createSession({
        planId: effectivePlanId(plan),
        subscriptionType: subscriptionType.toUpperCase(),
        memberCount: 1,
      });
      if (response.url) {
        window.location.href = response.url;
      } else if (
        response.paymentGateway === "RAZORPAY" &&
        response.subscriptionId
      ) {
        proceedRazorpaySubscription(
          response.subscriptionId,
          response.razorpayKeyId,
        );
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error("Error selecting plan:", error);
    }
  };

  const individualPlans = PLANS.filter(
    (p) => p.section === "individual" && (!disableFreePlan || p.id !== "FREE"),
  );
  const groupPlans = PLANS.filter((p) => p.section === "group");

  const PlanCard = (plan) => {
    const isPopular = !!plan.highlight;
    const price = getPrice(plan);
    return (
      <div
        key={plan.id}
        className={
          "relative rounded-2xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-lg " +
          (isPopular
            ? "bg-primary/5 border-primary/20 ring-2 ring-primary shadow-primary/10 scale-105 z-10 hover:border-primary/30"
            : "bg-white border-gray-100 hover:border-gray-200")
        }
      >
        {plan.ribbon && (
          <div className="absolute top-0 right-0 overflow-hidden w-28 h-28 pointer-events-none">
            <div
              className={
                "absolute top-7 -right-11 w-40 transform rotate-45 py-1.5 text-[10px] sm:text-xs font-bold tracking-wide uppercase shadow-lg " +
                (plan.ribbon.toLowerCase().includes("popular")
                  ? "bg-gradient-to-br from-primary via-primary to-blue-800 text-white"
                  : "bg-gradient-to-br from-secondary via-emerald-500 to-emerald-600 text-white")
              }
            >
              <div className="ribbon-marquee w-full overflow-hidden">
                <span className="ribbon-marquee-content drop-shadow-sm whitespace-nowrap">
                  {[1, 2].map((i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-3"
                    >
                      {plan.ribbon.toLowerCase().includes("popular") && (
                        <svg
                          className="w-3.5 h-3.5 shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      )}
                      {plan.ribbon.toLowerCase().includes("value") && (
                        <svg
                          className="w-3.5 h-3.5 shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {plan.ribbon}
                    </span>
                  ))}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className={`p-5 sm:p-6 ${plan.ribbon ? "pt-8 pr-8" : "pt-6"}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 font-dm-sans">
              {plan.name}
            </h3>
            {plan.badge && (
              <span className="whitespace-nowrap rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                {plan.badge}
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            {plan.description}
          </p>

          {/* Price */}
          <div className="mt-6">
            <div className="flex items-baseline text-slate-900">
              {plan.contact ? (
                <span className="text-2xl sm:text-3xl font-bold">
                  Let&apos;s Talk
                </span>
              ) : (
                <span className="text-2xl sm:text-3xl font-bold">
                  {currencySymbol}
                  {price}
                  <span className="text-base font-normal text-slate-500 ml-1">
                    /{currency}
                  </span>
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {plan.contact
                ? "Per-user or per-envelope pricing"
                : plan.section === "individual"
                  ? "Per user per month"
                  : "Per org per month"}
            </div>
          </div>

          {/* CTA */}
          <button
            type="button"
            className={
              "mt-6 w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 " +
              (plan.cta.variant === "primary"
                ? "bg-primary text-white hover:bg-primary/90 shadow-sm hover:shadow"
                : "border-2 border-primary text-primary hover:bg-primary/5")
            }
            onClick={() => {
              if (plan.contact) {
                setSelectedPlanForContactSale(plan);
                setShowContactSaleModal(true);
              } else {
                handlePlanSelection(plan, isAnnual ? "YEARLY" : "MONTHLY");
              }
            }}
          >
            {plan.cta.label}
          </button>

          {/* Features */}
          <div className="mt-6 pt-5 border-t border-gray-100">
            <button
              type="button"
              onClick={() => togglePlanFeatures(plan.id)}
              className="flex items-center justify-between w-full text-sm font-semibold text-slate-800 mb-3 hover:text-primary transition-colors"
            >
              <span>Key features</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className={`h-4 w-4 transition-transform ${expandedPlans.has(plan.id) ? "rotate-180" : ""}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>
            {expandedPlans.has(plan.id) && (
              <ul className="space-y-2.5">
                {plan.features.map((f, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2.5 text-sm text-slate-600"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      className="mt-0.5 h-4 w-4 text-primary shrink-0"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Billing + Plan type toggles */}
      {showBillingToggle && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          {/* Monthly / Yearly */}
          <div className="inline-flex items-center gap-3 sm:gap-4 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 transition-all duration-200">
            <span
              className={`text-sm font-medium transition-colors duration-200 ${!isAnnual ? "text-gray-900" : "text-gray-400"}`}
            >
              Monthly
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isAnnual}
              onClick={toggleBillingCycle}
              className={
                "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 " +
                (isAnnual ? "bg-primary" : "bg-gray-300")
              }
            >
              <span
                className={
                  "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 " +
                  (isAnnual ? "translate-x-5 mt-0.5" : "translate-x-1 mt-0.5")
                }
              />
            </button>
            <span
              className={`text-sm font-medium transition-colors duration-200 ${isAnnual ? "text-gray-900" : "text-gray-400"}`}
            >
              Annually
            </span>
            <span className="hidden sm:inline-flex items-center rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
              Save up to 30%
            </span>
          </div>

          {/* Individual / Group plans switch */}
          <div className="inline-flex items-center gap-2 sm:gap-3">
            <div className="inline-flex items-center gap-3 sm:gap-4 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 transition-all duration-200">
              <span
                className={`text-sm font-medium transition-colors duration-200 ${!showGroupPlans ? "text-gray-900" : "text-gray-400"}`}
              >
                Individual
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={showGroupPlans}
                onClick={() => setShowGroupPlans((v) => !v)}
                className={
                  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2 " +
                  (showGroupPlans ? "bg-primary" : "bg-gray-300")
                }
              >
                <span
                  className={
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 " +
                    (showGroupPlans
                      ? "translate-x-5 mt-0.5"
                      : "translate-x-1 mt-0.5")
                  }
                />
              </button>
              <span
                className={`text-sm font-medium transition-colors duration-200 ${showGroupPlans ? "text-gray-900" : "text-gray-400"}`}
              >
                Group
              </span>
            </div>
            <div
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/5 border border-primary/10 text-primary"
              title="Manage multiple companies under one organization with shared billing"
            >
              <svg
                className="w-4 h-4 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
              <span className="text-xs font-medium whitespace-nowrap">
                Multi-company
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Individual Plans ── */}
      {!showGroupPlans && (
        <div className="mt-12 w-full max-w-[90rem] mx-auto">
          <div className="mb-6">
            <h2 className="font-heading text-lg sm:text-xl font-bold text-gray-800">
              Individual Plans
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Perfect for individuals and small teams
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 items-center">
            {individualPlans.map((plan) => PlanCard(plan))}
          </div>
        </div>
      )}

      {/* ── Group / Multi-Company Plans ── */}
      {showGroupPlans && (
        <div className="mt-12">
          <div className="mb-6">
            <h2 className="font-heading text-lg sm:text-xl font-bold text-gray-800">
              Group &amp; Multi-Company Plans
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage multiple companies under one organization with shared
              billing
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {groupPlans.map((plan) => PlanCard(plan))}
          </div>
        </div>
      )}

      {/* Contact Sale Modal */}
      <ContactSaleModal
        isOpen={showContactSaleModal}
        onClose={() => {
          setShowContactSaleModal(false);
          setSelectedPlanForContactSale(null);
        }}
        plan={selectedPlanForContactSale}
        user={user}
      />
    </div>
  );
};

export default PricingPlansDashboard;
